"use client";

import Link from "next/link";
import { ArrowRight } from "lucide-react";
import { AppHeader } from "@/components/layout/AppHeader";
import { LegalFooter } from "@/components/LegalFooter";
import { useAuth } from "@/components/auth/AuthProvider";
import { FreeToolsNav } from "@/components/freeTools/FreeToolsNav";
import { HUB_CATEGORIES, type HubTool } from "@/components/hub/hubData";
import { ToolShowcaseCard } from "@/components/hub/ToolShowcaseCard";
import { PricingTierCards } from "@/components/pricing/PricingTierCards";
import { MATRIX_TIERS } from "@/components/pricing/pricingMatrix";
import { lockedToolLandingHref } from "@/lib/billing/planLabel";
import { useServiceSettings } from "@/hooks/useSiteSettings";
import { resolveToolPreviewImage } from "@/lib/toolPreviewImages";

/**
 * Cheapest paid tier's price text (e.g. "$14.99/mo"), read from the same
 * pricing data the comparison matrix uses, so the Hero's price mention can't
 * go stale independently of a real price change. `MATRIX_TIERS` prices are
 * plain display strings like "$14.99" — compared numerically here, but the
 * original string+period is what actually gets rendered.
 */
function cheapestPaidTierLabel(): string {
  const paidTiers = MATRIX_TIERS.filter((tier) => tier.key !== "free");
  const cheapest = paidTiers.reduce((min, tier) => {
    const value = Number(tier.price.replace(/[^0-9.]/g, ""));
    const minValue = Number(min.price.replace(/[^0-9.]/g, ""));
    return value < minValue ? tier : min;
  });
  return `${cheapest.price}${cheapest.period}`;
}

/**
 * Homepage for a signed-in user with no paid access — rendered by
 * HomePageSwitcher in place of CreatorHome. Every paid tool is shown locked;
 * this page never checks `permissions` because a free account has none of
 * them by definition.
 *
 * Also what an admin sees while using the "Preview as free user" toggle in
 * AppHeader (see src/lib/home/freePreview.ts): `viewingAsFreeUser` only
 * changes AppHeader's own nav branch below, never this page's content — a
 * real free user and a previewing admin are shown the exact same page.
 */
export default function FreeHome({
  viewingAsFreeUser = false,
}: {
  viewingAsFreeUser?: boolean;
} = {}) {
  const { user, isAdmin } = useAuth();
  const displayName =
    (user?.user_metadata?.full_name as string | undefined)?.trim() ||
    user?.email?.split("@")[0] ||
    "";

  return (
    <div className="lb-page min-h-screen">
      {/* Re-AND with the real isAdmin here too (see freePreview.ts) — a stray
          `true` from a caller that isn't actually gated on admin still can't
          do anything once it reaches AppHeader. */}
      <AppHeader viewingAsFreeUser={isAdmin && viewingAsFreeUser} />
      <main>
        <Hero name={displayName} />
        <FreeToolsSection />
        <PaidToolsSection />
        <PricingTierCards inAppCheckout />
      </main>
      <LegalFooter />
    </div>
  );
}

function Hero({ name }: { name: string }) {
  const fromPrice = cheapestPaidTierLabel();
  return (
    <section className="relative overflow-hidden">
      <div className="lb-container py-16 sm:py-24">
        <p className="font-mono text-[11px] uppercase tracking-[0.32em] text-muted-foreground">
          Free plan
        </p>
        <h1 className="lb-display mt-3 text-[3.4rem] font-semibold text-foreground sm:text-[4.6rem]">
          {name ? (
            <>
              Hello, <span className="text-accent">{name}</span>.
            </>
          ) : (
            <>Hello.</>
          )}
        </h1>
        <p className="mt-5 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
          Paint Booth, Easy Meta, PackBench and the rest of the Workshop are on paid plans, from{" "}
          {fromPrice}.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <Link
            href="#pricing"
            className="inline-flex items-center gap-1.5 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background shadow-sm transition hover:-translate-y-0.5 hover:bg-foreground/90 hover:shadow-md"
          >
            Compare plans
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function FreeToolsSection() {
  return (
    <section className="lb-container pb-12 sm:pb-16">
      <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
        These run in your browser. No subscription needed.
      </p>
      <FreeToolsNav />
    </section>
  );
}

/**
 * Every card on this page is locked (a free account has no paid features by
 * definition), so `lockedToolHref`'s /subscription destination would just
 * bounce straight back out to the landing pricing section (Subscription.tsx
 * redirects any free-plan visitor there). Skip that hop and link there
 * directly, keeping the upgrade params that target the right plan card.
 */
function freeUserLockedHref(featureKey: HubTool["featureKey"]): string {
  return lockedToolLandingHref(featureKey);
}

function PaidToolsSection() {
  // service_settings holds the same admin-managed screenshots the subscriber
  // hub shows (see CreatorHome's Favorites/Recent rows) — reuse that lookup
  // so a free user sees the real tool image instead of a blank/placeholder
  // box when one is configured.
  const { data: serviceSettings = [] } = useServiceSettings();

  return (
    <section className="lb-container pb-16 sm:pb-24">
      <div className="border-b border-border/50 pb-2">
        <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-muted-foreground">
          On a paid plan
        </p>
        <h2 className="mt-2 text-2xl font-semibold tracking-[-0.04em] text-foreground sm:text-[2rem]">
          The rest of the Workshop
        </h2>
      </div>
      <div className="mt-8 space-y-10">
        {HUB_CATEGORIES.map((category) => (
          <div key={category.id} className="space-y-3">
            <div className="flex items-baseline justify-between gap-3 border-b border-border/50 pb-2">
              <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-foreground/85">
                {category.label}
              </span>
              <span className="truncate text-[11px] text-muted-foreground/80">{category.hint}</span>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {category.tools.map((tool) => (
                <ToolShowcaseCard
                  key={tool.href}
                  tool={tool}
                  locked
                  lockedHref={freeUserLockedHref(tool.featureKey)}
                  imageUrl={resolveToolPreviewImage({
                    href: tool.href,
                    featureKey: tool.featureKey,
                    serviceSettings,
                  })}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
